import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

const RoomsPage = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "The Ocean View Inn",
    "description": "Comfortable oceanfront hotel and inn accommodations in Islamorada, Florida Keys with boat dock access",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/rooms`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "84500 Overseas Hwy",
      "addressLocality": "Islamorada",
      "addressRegion": "FL",
      "postalCode": "33036",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 24.9470979,
      "longitude": -80.6019239
    },
    "telephone": "(305) 664-8052",
    "email": "resinfo@theocean-view.com",
    "sameAs": [
      "https://www.facebook.com/The-OceanView-Motel-and-Sports-Pub-176655585715394",
      "https://www.instagram.com/oceanview_inn_pub/"
    ],
    "priceRange": "$$",
    "numberOfRooms": "28",
    "starRating": {
      "@type": "Rating",
      "ratingValue": "3"
    },
    "amenityFeature": [
      {
        "@type": "LocationFeatureSpecification",
        "name": "Free WiFi",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Free Parking",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Swimming Pool",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Boat Dock",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "On-site Restaurant",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "On-site Bar",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Waterfront Location",
        "value": true
      }
    ],
    "checkinTime": "15:00",
    "checkoutTime": "11:00"
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Hotel in Islamorada | The Ocean View Inn & Motel with Boat Dock Access"
        description="Book your stay at The Ocean View Inn, a waterfront hotel and motel in Islamorada, Florida Keys. Our oceanfront inn features comfortable lodging with boat dock access, swimming pool, on-site bar and restaurant, free WiFi, and free parking. Perfect for boaters and travelers seeking affordable motel-style accommodations in the heart of Islamorada. Experience true Keys hospitality at our family-owned hotel."
        keywords="hotel in Islamorada, Islamorada hotel, motel Islamorada, inn Islamorada, Islamorada lodging, Florida Keys hotel, waterfront hotel, boat dock hotel, Islamorada accommodations"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/rooms`}
        ogTitle="Islamorada Hotel & Inn with Boat Dock | The Ocean View Inn"
        ogDescription="Waterfront hotel and motel in Islamorada with boat dock access, pool, on-site bar and restaurant. Book your Florida Keys lodging today!"
        ogImage="https://public.readdy.ai/ai/img_res/853ee260d7c5e18468af2f999bbe4921.jpg"
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/rooms`}
        structuredData={structuredData}
        geoPosition="24.9470979;-80.6019239"
        geoRegion="US-FL"
        geoPlacename="Islamorada, Florida"
      />
      <Header />
      <main className="pt-20">
        <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://static.readdy.ai/image/a0544820e0625081891e0a711cac5596/42b1f6915e64bc2ee432484cbb122da8.png)',
            }}
          >
            <div className="absolute inset-0 bg-ov-sand-900/60"></div>
          </div>
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 font-serif">Our Hotel & Inn</h1>
            <p className="text-xl text-ov-cream-100">
              Comfortable waterfront hotel accommodations with boat dock access and tropical flair in the heart of Islamorada
            </p>
          </div>
        </section>

        <section className="py-20 bg-ov-cream-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-4xl font-bold text-ov-sand-900 mb-6 font-serif">
                Your Home Away From Home in Islamorada
              </h2>
              <p className="text-lg text-ov-sand-600 leading-relaxed">
                The Ocean View Inn offers clean, comfortable motel-style accommodations with a tropical flair. Our waterfront hotel features boat dock access for guests arriving by water, making us the perfect choice for boaters exploring the Florida Keys. Whether you're looking for a weekend getaway or a longer stay at our Islamorada inn, you'll find everything you need to enjoy the perfect Florida Keys vacation.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/a0544820e0625081891e0a711cac5596/9b5c52e7dd23d238e0f17694adaf654c.png"
                  alt="Ocean View Inn guest room with garden view"
                  className="w-full h-[500px] object-cover object-top"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h3 className="text-3xl font-bold text-ov-sand-900 mb-6">Hotel & Motel Amenities</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-anchor-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">Boat Dock Access</h4>
                      <p className="text-ov-sand-600">Direct water access with boat docking facilities for our hotel guests</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-air-conditioner-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">Climate Control</h4>
                      <p className="text-ov-sand-600">Air conditioning and heating for year-round comfort in our inn</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-tv-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">Entertainment</h4>
                      <p className="text-ov-sand-600">Flat-screen TV with cable channels in every motel room</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-wifi-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">Free Wi-Fi</h4>
                      <p className="text-ov-sand-600">Stay connected with complimentary wireless internet throughout our hotel</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-parking-box-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">Free Parking</h4>
                      <p className="text-ov-sand-600">Complimentary on-site parking for all hotel and motel guests</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-swimming-pool-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">Pool Access</h4>
                      <p className="text-ov-sand-600">Beautiful swimming pool and BBQ area at our waterfront inn</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-ov-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-restaurant-line text-2xl text-ov-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ov-sand-900 mb-1">On-Site Dining</h4>
                      <p className="text-ov-sand-600">Sports pub, bar, and restaurant steps from your hotel room</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-ov-teal-50 to-ov-blue-50 rounded-2xl p-8 md:p-12">
              <div className="max-w-3xl mx-auto text-center">
                <h3 className="text-3xl font-bold text-ov-sand-900 mb-4">Ready to Book Your Stay at Our Islamorada Hotel?</h3>
                <p className="text-lg text-ov-sand-600 mb-8">
                  Check availability and reserve your room at The Ocean View Inn today. Experience the best waterfront hotel and motel accommodations in the Florida Keys with boat dock access!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="https://hotels.cloudbeds.com/en/reservation/KsGpNF"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-ov-teal-600 to-ov-blue-500 text-white rounded-lg font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    Check Availability
                  </a>
                  <a
                    href="tel:+13056648052"
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-ov-sand-900 rounded-lg font-semibold text-lg border-2 border-ov-sand-200 hover:border-ov-teal-600 hover:text-ov-teal-600 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RoomsPage;
